import { CommonModule } from '@angular/common';
import { Component, computed, ElementRef, HostListener, OnInit, signal } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { Case } from '../../../core/models/case.model';
import { AuthService } from '../../../core/services/auth.service';
import { CaseService } from '../../../core/services/case.service';
import { ModalService } from '../../../core/services/modal.service';
import { SearchFilterComponent } from '../../../features/dashboard/components/search-filter/search-filter.component';
import { debounceTime, distinctUntilChanged, Subject, switchMap } from 'rxjs';
import { ThemeToggleComponent } from '@mlc/shared/theme-toggle/theme-toggle.component';
import { toSignal } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [RouterLink, CommonModule, SearchFilterComponent, ThemeToggleComponent],
  templateUrl: './header.html'
})
export class Header implements OnInit {
  isDropdownOpen = signal(false);
  searchTerm = signal<string>('');
  searchResults = signal<Case[]>([]);
  showSearchResults = signal<boolean>(false);
  private searchTerms = new Subject<string>();
  @HostListener('document:click', ['$event'])
  private loggedInUserSignal = toSignal(this.authService.getLoggedInUser());
  currentUser = computed(() => this.loggedInUserSignal());
  firmName = computed(() => {
    const user = this.currentUser();
    return user?.firmName || '';
  });
  orgName = computed(() => {
    const user = this.currentUser();
    return user?.defaults?.orgName || '';
  });

  constructor(
    private authService: AuthService,
    private router: Router,
    private modalService: ModalService,
    private elementRef: ElementRef,
    private caseService: CaseService
  ) { }

  ngOnInit(): void {
    this.searchTerms.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap((term: string) => this.caseService.searchCases(term))
    ).subscribe(results => {
      this.searchResults.set(results);
      this.showSearchResults.set(this.searchTerm().length > 0);
    });
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: Event): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.isDropdownOpen.set(false);
      this.showSearchResults.set(false);
    }
  }

  toggleDropdown(): void {
    this.isDropdownOpen.set(!this.isDropdownOpen());
  }

  onSearchChange(term: string): void {
    this.searchTerm.set(term);
    this.searchTerms.next(term);
    this.showSearchResults.set(term.length > 0);
  }

  onSearchFocus(): void {
    this.showSearchResults.set(this.searchTerm().length > 0);
  }

  onSearchBlur(): void {
    // Delay hiding to allow click on results
    setTimeout(() => this.showSearchResults.set(false), 3000);
  }

  onResultClick(caseId: string): void {
    this.router.navigate(['/case', caseId]);
    this.showSearchResults.set(false);
    this.searchTerm.set(''); // Clear search term after selection
  }

  openHelpDesk(): void {
    this.modalService.openHelpDeskModal();
    this.isDropdownOpen.set(false);
  }

  logout(): void {
    this.authService.logout()
      .then(() => {
        this.router.navigate(['/login']);
        this.isDropdownOpen.set(false); // Close dropdown on logout
      })
      .catch((error) => {
        console.error('Logout failed:', error);
        // Optionally, display an error message to the user
      });
  }
}
